import { createPlugin, type SlotName, type SlotType } from "zudoku";
import { Suspense, type ComponentType, type ReactNode } from "react";
import {
  discoverInjections,
  type InjectionsBySlot,
} from "./discover-injections.js";
import { TARGETS } from "./targets.js";

export type InjectionLoader = () => Promise<{ default: ComponentType }>;

export type SlotInjectionPluginOptions = {
  /**
   * Vite import.meta.glob result for injection files.
   * Expected path shape under the chosen directory: target/id/slot-name.mdx
   * Pass the glob from zudoku.config (the plugin does not hardcode a directory).
   */
  loaders: Record<string, InjectionLoader>;
};

function renderInjectionsForSlot(
  bySlot: InjectionsBySlot,
  slotName: SlotName,
  pathname: string,
  basePath: string | undefined,
): ReactNode {
  const byTarget = bySlot.get(slotName);
  if (!byTarget) return null;

  const nodes: ReactNode[] = [];

  for (const [target, byId] of byTarget) {
    const id = TARGETS[target].resolveId(pathname, basePath);
    if (!id) continue;
    const Lazy = byId.get(id);
    if (!Lazy) continue;
    nodes.push(
      <Suspense key={`${target}:${id}:${slotName}`} fallback={null}>
        <Lazy />
      </Suspense>,
    );
  }

  if (nodes.length === 0) return null;

  return <div className={`injected__${slotName}`}>{nodes}</div>;
}

function chainSlotHandlers(
  existing: SlotType | undefined,
  injection: SlotType,
): SlotType {
  if (!existing) return injection;

  if (typeof existing === "function") {
    return (props) => (
      <>
        {existing(props)}
        {typeof injection === "function" ? injection(props) : injection}
      </>
    );
  }

  return (props) => (
    <>
      {existing}
      {typeof injection === "function" ? injection(props) : injection}
    </>
  );
}

/**
 * Injects MDX/MD files into Zudoku slots. Pass Vite glob loaders from config —
 * the injections directory is not hardcoded in the plugin.
 *
 * Path shape inside the glob: target/id/slot-name.mdx
 * Targets (and their valid slots) are defined in ./targets.ts.
 */
export const slotInjectionPlugin = createPlugin(
  "slot-injection",
  (options: SlotInjectionPluginOptions) => {
    const injectionsBySlot = discoverInjections(options.loaders);

    return {
      transformConfig: ({ config, merge }) => {
        if (injectionsBySlot.size === 0) {
          return config;
        }

        const slotHandlers: Partial<Record<SlotName, SlotType>> = {};

        for (const slotName of injectionsBySlot.keys()) {
          const injectionHandler: SlotType = (slotProps) =>
            renderInjectionsForSlot(
              injectionsBySlot,
              slotName,
              slotProps.location.pathname,
              config.basePath,
            );

          slotHandlers[slotName] = chainSlotHandlers(
            config.slots?.[slotName],
            injectionHandler,
          );
        }

        return merge({ slots: slotHandlers });
      },
    };
  },
);
